import { exchangeCode, refreshToken as brokerRefresh } from "@/api/brokerClient";
import { buildAuthorizeUrl, generateCodeVerifier, parseCallback } from "@/use/usePkce";
import { clearApiCache, getJson } from "@/utils/http/apiClient";
import { matchConstructor } from "@/utils/constructors/matchConstructor";
import { setMatchPlayersAverageRating } from "@/utils/players/playersAverageRating";
import { getPlayersPool } from "@/utils/players/playersPool";
import { setMatchPlayersStatus } from "@/utils/players/playersStatus";
import { computed, onBeforeMount, readonly, ref, watch } from "vue";

const token = ref("");
const refreshTokenValue = ref("");
const user = ref({});
const loginEnded = ref(false);
const liveData = ref({});
let refreshInFlight = null;

// Durées de cache des données peu changeantes
const LEAGUE_CACHE_TTL_MS = 72 * 60 * 60 * 1000; // 3 jours
const TEAM_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes : les bonus disponibles évoluent en cours de journée
// Une journée terminée est immuable, mais le cache est en mémoire (vidé à la déconnexion et au rechargement), donc cette durée vaut en pratique pour la session
const GAME_WEEK_MATCHES_CACHE_TTL_MS = 72 * 60 * 60 * 1000; // 3 jours

export function useMPG () {

  /**
   * Méthode de connexion à MPG
   */
  onBeforeMount(() => {
    token.value = localStorage.getItem("mpg-token");
    refreshTokenValue.value = localStorage.getItem("mpg-refresh-token") ?? "";
  });
  const isConnected = computed(() => {
    return Boolean(token.value);
  });
  const signInWithToken = (newToken) => {
    token.value = newToken;
    localStorage.setItem("mpg-token", newToken);
  };
  const storeTokens = ({ access_token, refresh_token, expires_in }) => {
    token.value = access_token;
    localStorage.setItem("mpg-token", access_token);
    if (refresh_token) {
      refreshTokenValue.value = refresh_token;
      localStorage.setItem("mpg-refresh-token", refresh_token);
    }
    localStorage.setItem("mpg-token-expiry", String(Date.now() + (expires_in ?? 3600) * 1000));
  };
  // Prépare l'URL d'autorisation (calcul PKCE asynchrone) SANS rien écrire dans le
  // stockage. À appeler au montage, pour que le clic puisse rester synchrone : un
  // await entre le clic et window.open casse le lien avec le geste utilisateur et
  // fait bloquer l'ouverture (silencieusement) sur mobile.
  const prepareLogin = async () => {
    const verifier = generateCodeVerifier();
    const state = generateCodeVerifier();
    const nonce = generateCodeVerifier();
    const url = await buildAuthorizeUrl({
      verifier,
      state,
      nonce,
    });
    return {
      url,
      verifier,
      state,
    };
  };
  // Mémorise le verifier/state au moment réel du démarrage de la connexion (le clic),
  // de façon synchrone. Écrit au clic (et non au montage) pour survivre à un
  // rechargement d'onglet entre le clic et le collage du code.
  const beginLogin = ({ verifier, state }) => {
    localStorage.setItem("mpg-pkce-verifier", verifier);
    localStorage.setItem("mpg-pkce-state", state);
  };
  const completeLogin = async (callbackInput) => {
    const parsed = parseCallback(callbackInput);
    if (! parsed) {
      throw new Error("Code de connexion introuvable");
    }
    const expectedState = localStorage.getItem("mpg-pkce-state");
    const verifier = localStorage.getItem("mpg-pkce-verifier");
    if (! expectedState || ! verifier) {
      throw new Error("Session de connexion expirée, relancez la connexion");
    }
    // Un code collé seul (parsed.state === "") n'embarque pas de state : la protection repose alors sur PKCE.
    // Dès qu'un state est fourni, il doit correspondre à celui généré par prepareLogin (anti-CSRF).
    if (parsed.state && parsed.state !== expectedState) {
      throw new Error("État de connexion invalide");
    }
    try {
      storeTokens(await exchangeCode(parsed.code, verifier));
    } finally {
      localStorage.removeItem("mpg-pkce-verifier");
      localStorage.removeItem("mpg-pkce-state");
    }
  };
  const refreshStoredToken = async () => {
    try {
      storeTokens(await brokerRefresh(refreshTokenValue.value));
    } catch (error) {
      // Le token reste en l'état : un appel de données ultérieur recevra un 401 géré par le flux existant
      console.warn("Échec du renouvellement du token de connexion", error);
    } finally {
      refreshInFlight = null;
    }
  };
  const ensureFreshToken = async () => {
    if (refreshInFlight) {
      return refreshInFlight;
    }
    const expiry = Number(localStorage.getItem("mpg-token-expiry") ?? 0);
    if (Date.now() < expiry - 60_000 || ! refreshTokenValue.value) {
      return;
    }
    // Promesse partagée : la rotation du refresh token invaliderait un second renouvellement simultané
    refreshInFlight = refreshStoredToken();
    return refreshInFlight;
  };
  const resetToken = () => {
    token.value = undefined;
    refreshTokenValue.value = "";
    localStorage.removeItem("mpg-token");
    localStorage.removeItem("mpg-refresh-token");
    localStorage.removeItem("mpg-token-expiry");
    localStorage.removeItem("mpg-pkce-verifier");
    localStorage.removeItem("mpg-pkce-state");
    clearApiCache();
  };

  /**
   * Récupération des infos de l'utilisateur
  */
  watch(token, () => {
    if (token.value) {
      getUser();
    }
  });

  const getUser = async () => {
    await ensureFreshToken();
    const response = await fetch("https://api.mpg.football/user", {
      method: "GET",
      headers: {
        accept: "application/json, text/plain, */*",
        authorization: token.value,
      },
      body: null,
    });
    if (response.status === 200) {
      user.value = await response.json();
      loginEnded.value = true;
    } else {
      resetToken();
    }
  };
  const haveLiveRating = computed(() => {
    return user.value?.applicationsData?.mpg.gameOptions.liveRatingAvailable;
  });

  /**
   * Matches Live
   */
  watch(token, () => {
    if (token.value) {
      getLiveData();
    }
  });

  const getLiveData = async () => {
    await ensureFreshToken();
    liveData.value = await getJson("https://api.mpg.football/live", { token: token.value });
  };

  const liveLeagues = computed(() => {
    const liveLeagues = liveData.value?.orderedLeagueDivisionItems;
    return liveLeagues ? Object.values(liveLeagues).filter((league) => {
      return league.liveState && ! league.liveDivisionGameWeek.isPlayoffs;
    }) : [];
  });

  const livePlayoffs = computed(() => {
    const livePlayoffs = liveData.value?.orderedLeagueDivisionItems;
    return livePlayoffs ? Object.values(livePlayoffs).filter((playoff) => {
      return playoff.liveState && playoff.liveDivisionGameWeek.isPlayoffs;
    }) : [];
  });

  const liveTournaments = computed(() => {
    const liveTournaments = liveData.value?.orderedTournamentItems;
    return liveTournaments ? Object.values(liveTournaments).filter(tournament => tournament.liveState) : [];
  });

  /**
   * Matchs data
   */
  const getLeagueMatch = async (matchId) => {
    const matchData = await getJson(`https://api.mpg.football/division-match/${matchId}`, { token: token.value });

    const [leagueData, homeTeamData, awayTeamData] = await Promise.all([
      getLeagueInfos(getLeagueIdFromMatchId(matchId)),
      getTeamInfos(matchData.home.teamId),
      getTeamInfos(matchData.away.teamId),
    ]);

    const match = matchConstructor({
      ...matchData,
      isOneWay: leagueData.isOneWay,
      home: {
        ...matchData.home,
        ...homeTeamData,
      },
      away: {
        ...matchData.away,
        ...awayTeamData,
      },
    });

    return setPlayersInfos(match, matchData);
  };

  const getTournamentMatch = async (matchId) => {
    const matchData = await getJson(`https://api.mpg.football/tournament-match/${matchId}`, { token: token.value });
    const match = matchConstructor(matchData);
    match.isTournament = true;

    return setPlayersInfos(match, matchData);
  };

  /**
   * Renvoi tous les matchs d'une journée de division, compositions comprises
   * Un seul appel suffit à reconstituer toute la journée écoulée.
   * @param {String} divisionId l'identifiant de la division
   * @param {Number} gameWeek le numéro de la journée
   * @returns {Promise<Array>} les matchs de la journée
   */
  const getGameWeekMatches = async (divisionId, gameWeek) => {
    try {
      const data = await getJson(`https://api.mpg.football/division/${divisionId}/game-week/${gameWeek}/matches`, {
        token: token.value,
        cacheTtlMs: GAME_WEEK_MATCHES_CACHE_TTL_MS,
      });
      return data?.divisionMatches || [];
    } catch (error) {
      console.error(`Failed to fetch matches for division ${divisionId} game week ${gameWeek}`, error);
      return [];
    }
  };

  const setPlayersInfos = async (match, matchData) => {
    const matchWithAverages = await setMatchPlayersAverageRating(match, matchData.championshipMatches, getPlayerInfos);
    const finalMatch = setMatchPlayersStatus(matchWithAverages, matchData.championshipMatches);
    return finalMatch;
  };

  /**
   * League data
   */
  const getLeagueIdFromMatchId = (matchId) => {
    return "mpg_league_" + matchId.split("_")[3];
  };
  const getLeagueInfos = async (leagueId) => {
    if (! leagueId) {
      return { isOneWay: false };
    }
    const data = await getJson(`https://api.mpg.football/league/${leagueId}`, {
      token: token.value,
      cacheTtlMs: LEAGUE_CACHE_TTL_MS,
    });
    return {
      id: data?.id,
      name: data?.name,
      isOneWay: Boolean(data?.gameSettings?.isOneWay),
    };
  };

  /**
   * Get all leagues (including non-live) for best team calculation
   */
  const allLeagues = computed(() => {
    const leagues = liveData.value?.orderedLeagueDivisionItems;
    return leagues ? Object.values(leagues) : [];
  });

  /**
   * Team data
   */
  const getTeamInfos = async (teamId) => {
    const data = await getJson(`https://api.mpg.football/team/${teamId}`, {
      token: token.value,
      cacheTtlMs: TEAM_CACHE_TTL_MS,
    });
    return {
      name: data.name,
      abbreviation: data.abbreviation,
      availableBonuses: data.availableBonuses,
    };
  };

  /**
   * Championship players pool
   */
  const fetchChampionshipPlayersPool = async (championshipId) => {
    return getJson(`https://api.mpg.football/championship-players-pool/${championshipId}`, { token: token.value });
  };

  const getChampionshipPlayersPool = async (championshipId) => {
    return getPlayersPool(championshipId, fetchChampionshipPlayersPool);
  };

  /**
   * Players data
   */
  const getPlayerInfos = async (playerId, championshipId, season) => {
    const data = await getJson(`https://api.mpg.football/championship-player-stats/${playerId}/${season}`, { token: token.value });
    const SUPERLEAGUE_ID = 7;
    if (championshipId === SUPERLEAGUE_ID) {
      championshipId = data.aggregatedChampionshipsCorrelations[SUPERLEAGUE_ID];
    }
    return data.championships[championshipId].keySeasonStats || {};
  };

  /**
   * Team players performances (for best team calculation)
   */
  const getTeamPlayersPerformances = async (divisionId, matchId, teamId) => {
    try {
      const result = await getJson(`https://api.mpg.football/division/${divisionId}/match/${matchId}/team/${teamId}/match-team-formation`, { token: token.value });
      return result.playersMatches;
    } catch (error) {
      console.error(`Failed to fetch team formation for team ${teamId}`, error);
      return null;
    }
  };

  /**
   * Ranking data
   */
  const getRanking = async (divisionId) => {
    let data;
    try {
      data = await getJson(`https://api.mpg.football/division/${divisionId}/ranking/standings`, { token: token.value });
    } catch (error) {
      console.error(`Failed to fetch ranking for division ${divisionId}`, error);
      return null;
    }

    if (! data.standings || ! Array.isArray(data.standings)) {
      console.warn(`No standings data for division ${divisionId}`);
      return null;
    }

    // Fetch team details to get names
    const teamDetailsPromises = data.standings.map(s => getTeamInfos(s.teamId));
    const teamsDetails = await Promise.all(teamDetailsPromises);

    const teamsNames = {};
    data.standings.forEach((s, index) => {
      teamsNames[s.teamId] = teamsDetails[index].name;
    });

    return {
      ...data,
      teamsNames,
    };
  };

  return {
    prepareLogin,
    beginLogin,
    completeLogin,
    ensureFreshToken,
    signInWithToken,
    storeTokens,
    refreshTokenValue: readonly(refreshTokenValue),
    user: readonly(user),
    isConnected,
    loginEnded: readonly(loginEnded),
    haveLiveRating,
    liveLeagues,
    livePlayoffs,
    liveTournaments,
    allLeagues,
    getLeagueMatch,
    getTournamentMatch,
    getGameWeekMatches,
    getLeagueInfos,
    getTeamInfos,
    getRanking,
    getTeamPlayersPerformances,
    getPlayerInfos,
    getChampionshipPlayersPool,
  };
}